import isEqual from 'lodash.isequal';
import moment from 'moment';

const OPEN_24_HOURS = 'Open 24 hours';
const TO_SENTENCE = 'to';

export const formatDaysOpen = (locationHours, hoursFormat, open24HoursOverride, toOverride) => {
  const open24HoursText = open24HoursOverride || OPEN_24_HOURS;
  const toSentenceText = toOverride || TO_SENTENCE;
  const formattedArray = [];
  let lastDayIterated = null;

  const formatOpenCloseTimes = (time_open, time_close) => {
    if (hoursFormat === 'format_24_hours') {
      return `${time_open} - ${time_close}`;
    }

    const formattedOpen = moment(time_open, "HH:mm").format("h:mm A", { trim: 'small' });
    const formattedClose = moment(time_close, "HH:mm").format("h:mm A", { trim: 'small' });

    return `${formattedOpen} - ${formattedClose}`;
  }

  for (const locationHour of locationHours) {
    if (lastDayIterated && locationHour.day === lastDayIterated.day) {
      // If multiple open hours on same day
      const existingDayObject = formattedArray[formattedArray.length - 1];
      const existingOpenTime = existingDayObject.time_open;
      const existingOpenHour = Number.parseInt(existingOpenTime.split(':')[0], 10);
      const newOpenHour = locationHour.time_open ? Number.parseInt(locationHour.time_open.split(':')[0], 10) : 24;
      const newOpenTime = formatOpenCloseTimes(locationHour.time_open, locationHour.time_close);

      if (existingOpenHour < newOpenHour) {
        existingDayObject.formatted_time.push(newOpenTime);
      } else if (existingOpenTime !== newOpenTime) {
        existingDayObject.formatted_time.unshift(newOpenTime);
      }
    } else if (locationHour.time_open === '00:00' && locationHour.time_close === '00:00') {
      // If open 24 hours
      formattedArray.push({
        ...locationHour,
        formatted_time: [open24HoursText],
        formatted_day: locationHour.day_full_name
      });
    } else if (locationHour.time_open === 'CLOSED') {
      // If closed this day
      formattedArray.push({
        ...locationHour,
        formatted_time: [locationHour.time_close],
        formatted_day: locationHour.day_full_name
      });
    } else {
      formattedArray.push({
        ...locationHour,
        formatted_time: [formatOpenCloseTimes(locationHour.time_open, locationHour.time_close)],
        formatted_day: locationHour.day_full_name
      });
    }

    lastDayIterated = locationHour;
  }

  // Sort by day of week, with Sunday at the end
  const sortedArray = formattedArray.sort((a, b) => {
    const dayA = a.day === 0 ? 7 : a.day;
    const dayB = b.day === 0 ? 7 : b.day;

    return dayA - dayB;
  });

  // Split into weekdays and weekend
  const weekdays = sortedArray.filter(({ day }) => day >= 1 && day <= 5);
  const saturday = sortedArray.find(({ day }) => day === 6);
  const sunday = sortedArray.find(({ day }) => day === 0);

  // Check if all weekdays have identical hours
  const weekdaysIdentical = weekdays.length === 5 && weekdays.every(day =>
    isEqual(day.formatted_time, weekdays[0].formatted_time)
  );

  if (!weekdaysIdentical) {
    // If weekdays aren't identical, show each day separately
    return sortedArray;
  }

  // Check if Saturday matches weekday hours
  const saturdayMatchesWeekdays = saturday && isEqual(saturday.formatted_time, weekdays[0].formatted_time);

  // Check if Sunday matches weekday hours (only relevant if Saturday matches)
  const allDaysIdentical = saturdayMatchesWeekdays && sunday &&
    isEqual(sunday.formatted_time, weekdays[0].formatted_time);

  const finalArray = [];

  if (allDaysIdentical) {
    // If all days match, show Monday to Sunday
    finalArray.push({
      ...weekdays[0],
      formatted_day: `${weekdays[0].day_full_name} ${toSentenceText} ${sunday.day_full_name}`
    });
  } else if (saturdayMatchesWeekdays) {
    // If weekdays + Saturday match, show Monday to Saturday + Sunday
    finalArray.push({
      ...weekdays[0],
      formatted_day: `${weekdays[0].day_full_name} ${toSentenceText} ${saturday.day_full_name}`
    });

    if (sunday) finalArray.push(sunday);
  } else {
    // If only weekdays match, show Monday to Friday + weekend days
    finalArray.push({
      ...weekdays[0],
      formatted_day: `${weekdays[0].day_full_name} ${toSentenceText} ${weekdays[4].day_full_name}`
    });

    if (saturday) finalArray.push(saturday);
    if (sunday) finalArray.push(sunday);
  }

  return finalArray;
}

export const isShopifyEmbed = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('embed') === 'true';
}

export const triggeriFramePostMessage = (height) => {
  // Notify parent iframe to resize height if mobile device
  let iframeHeight = height;
  const embedEl = document.getElementsByClassName('closeby-overlay')[0];

  if (!embedEl) return;
  if (!iframeHeight) iframeHeight = embedEl.offsetHeight;

  // Check if we're in a Shopify embed context (embed=true parameter)
  const isEmbedded = isShopifyEmbed();
  const isMobileDevice = window.innerWidth < 768;

  // For Shopify embeds on mobile, limit the height to 600px to prevent scrolling issues
  if (isEmbedded && isMobileDevice) {
    iframeHeight = Math.min(iframeHeight, 600);
  }

  window.parent.postMessage({ action: 'resizeMobileHeight', pageHeight: iframeHeight }, '*');
}
